import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { Button, Modal, Form } from "react-bootstrap";
import Loader from "../Common/Loader";
import { Html5Qrcode } from "html5-qrcode";
import MedicineDropdown from "../AppsMenu/Patients/Components/MedicineDropdown";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { addInwardStockAction, getInwardStockDetailsAction } from "../../../store/actions/inwardStockAction";
import {
  handleSuccess,
  showErrorAlert,
} from "../../../services/ErrorHandlingService";
import swal from "sweetalert";

const AddInwardStock = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const branches = useSelector((state) => state.branches.branches || []);

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [cartons, setCartons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Validation errors state
  const [errors, setErrors] = useState({});
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    medicine_id: "",
    medicine_name: "",
    batch_no: "",
    carton_id: "",
    qty: "",
    manufacturing_date: "",
  });

  // Fetch branches on load
  useEffect(() => {
    dispatch(getBranchesAction());
  }, [dispatch]);
  
  // Note: Medicine loading is handled by MedicineDropdown component with lazy loading

  // Fetch inward stock details when in view mode
 // Fetch inward stock details when in view mode
useEffect(() => {
  const fetchInwardDetails = async () => {
    if (location.state?.isViewMode && location.state?.inward_id) {
      setIsViewMode(true);
      setLoading(true);
      try {
        const result = await dispatch(getInwardStockDetailsAction(location.state.inward_id));
        console.log("Fetched details result:", result);
        
        if (result.success && result.data) {
          const details = result.data;
          
          // Set branch (if branch_name is available)
          if (details.branch_name) {
            const branchObj = branches.find(b => b.branch_name === details.branch_name);
            if (branchObj) {
              setSelectedBranch({
                value: branchObj.branch_id,
                label: branchObj.branch_name,
              });
            }
          }
          
          // Set cartons from scanqrcodedata (which is now the array of items)
          if (details.scanqrcodedata && details.scanqrcodedata.length > 0) {
            const formattedCartons = details.scanqrcodedata.map((item, idx) => ({
              medicine_id: item.medicine_id,
              medicine_name: item.medicine_name,
              batch_no: item.batch_no,
              carton_id: item.carton_id,
              qty: item.qty,
              manufacturing_date: convertToISOFormat(item.mfg_date),
            }));
            setCartons(formattedCartons);
            console.log("Formatted cartons:", formattedCartons);
          }
        }
      } catch (error) {
        console.error("Failed to fetch inward details:", error);
        swal("Error", "Failed to load inward stock details", "error");
      } finally {
        setLoading(false);
      }
    }
  };
  
  fetchInwardDetails();
}, [location.state, branches, dispatch]);
  // Convert DD-MM-YYYY to YYYY-MM-DD
  const convertToISOFormat = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    return dateStr;
  };

  const branchOptions = branches.map((b) => ({
    value: b.branch_id,
    label: b.branch_name,
  }));

  // Scanner functionality
  useEffect(() => {
    let scanner;
    let isMounted = true;
    let isScanning = false;

    const stopScanner = async () => {
      if (scanner && isScanning) {
        try {
          await scanner.stop();
          isScanning = false;
          const readerElement = document.getElementById("qr-reader");
          if (readerElement) {
            readerElement.innerHTML = "";
          }
        } catch (err) {
          console.error("Error stopping scanner:", err);
        }
      }
    };

    const startScanner = async () => {
      if (isScanning) return;
      
      try {
        scanner = new Html5Qrcode("qr-reader");
        isScanning = true;

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        };

        const onScanSuccess = (decodedText) => {
          if (isMounted && isScanning) {
            handleScanSuccess(decodedText);
            stopScanner();
            setShowScanner(false);
          }
        };

        const onScanFailure = () => {};

        await scanner.start({ facingMode: "user" }, config, onScanSuccess, onScanFailure).catch(async () => {
          try {
            await scanner.start({ facingMode: "environment" }, config, onScanSuccess, onScanFailure);
          } catch (err) {
            console.error("Scanner error:", err);
            if (isMounted) {
              setShowScanner(false);
              setCameraError(true);
            }
          }
        });
      } catch (err) {
        console.error("Scanner initialization error:", err);
        if (isMounted) {
          setShowScanner(false);
          setCameraError(true);
        }
      }
    };

    if (showScanner && !cameraError && isMounted) {
      startScanner();
    }

    return () => {
      isMounted = false;
      stopScanner();
    };
  }, [showScanner, cameraError]);

  const checkCameraPermissions = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera API not supported");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
      setShowScanner(true);
      setCameraError(false);
    } catch (err) {
      console.error("Camera permission denied:", err);
      setCameraError(true);
    }
  };

  const handleScanSuccess = (decodedText) => {
    console.log("Scanned:", decodedText);
    setQrCode(decodedText);
    setShowScanner(false);
    
    setTimeout(() => {
      handleScan(decodedText);
    }, 300);
  };

  // const handleScan = (scannedCode) => {
  //   const code = scannedCode || qrCode;
  //   if (!code) return;
  
  //   try {
  //     let scannedData = null;
  
  //     try {
  //       scannedData = JSON.parse(code);
  //     } catch (e) {
  //       scannedData = { carton_id: code };
  //     }
  
  //     const isCartonQR =
  //       String(scannedData.is_carton_qr || "")
  //         .trim()
  //         .toLowerCase() === "yes";
  
  //     const existingIndex = cartons.findIndex(
  //       (item) =>
  //         String(item.carton_id) === String(scannedData.carton_id) &&
  //         String(item.medicine_id) === String(scannedData.medicine_id)
  //     );
  
  //     // Case 1:
  //     // Carton QR scanned again
  //     if (isCartonQR && existingIndex !== -1) {
  //       handleSuccess("Medicine already added");
  //       setQrCode("");
  //       return;
  //     }
  
  //     // Case 2:
  //     // Same medicine + carton scanned again
  //     // Increase quantity
  //     if (!isCartonQR && existingIndex !== -1) {
  //       const updatedCartons = [...cartons];
  
  //       updatedCartons[existingIndex] = {
  //         ...updatedCartons[existingIndex],
  //         qty:
  //           Number(updatedCartons[existingIndex].qty || 0) +
  //           Number(scannedData.qty || 0),
  //       };
  
  //       setCartons(updatedCartons);
  
  //       handleSuccess("Quantity updated successfully");
  
  //       setQrCode("");
  //       return;
  //     }
  
  //     let mfg_date = scannedData.mfg_date || "";
  
  //     if (mfg_date && typeof mfg_date === "string") {
  //       const parts = mfg_date.split("-");
  
  //       if (parts.length === 3) {
  //         const day = parts[0];
  //         const month = parts[1];
  //         const year = parts[2];
  
  //         if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
  //           mfg_date = `${year}-${String(month).padStart(
  //             2,
  //             "0"
  //           )}-${String(day).padStart(2, "0")}`;
  //         }
  //       }
  //     }
  
  //     // Case 3:
  //     // New medicine
  //     const newCarton = {
  //       carton_id:
  //         scannedData.carton_id ||
  //         scannedData.carton_code ||
  //         code,
  
  //       medicine_id: scannedData.medicine_id || "",
  //       medicine_name: scannedData.medicine_name || "",
  //       batch_no: scannedData.batch_no || "",
  //       qty: scannedData.qty || "",
  //       manufacturing_date: mfg_date,
  //     };
  
  //     setCartons([...cartons, newCarton]);
  
  //     handleSuccess("Medicine added successfully");
  
  //     setErrors({
  //       ...errors,
  //       scan_error: "",
  //     });
  
  //     setQrCode("");
  //   } catch (error) {
  //     console.error("Error processing scan:", error);
  
  //     showErrorAlert("Invalid QR code format");
  
  //     setErrors({
  //       ...errors,
  //       scan_error: "Invalid QR code format",
  //     });
  
  //     setTimeout(() => {
  //       setErrors((prev) => ({
  //         ...prev,
  //         scan_error: "",
  //       }));
  //     }, 3000);
  //   }
  // };
  const handleScan = (scannedCode) => {
    const code = scannedCode || qrCode;
  
    if (!code) return;
  
    try {
      let scannedData;
  
      try {
        scannedData = JSON.parse(code);
      } catch {
        scannedData = { carton_id: code };
      }
  
      console.log("QR Data:", scannedData);
  
      const isCartonQR =
        String(scannedData?.is_carton_qr || "")
          .trim()
          .toLowerCase() === "yes";
  
      const qtyToAdd = isCartonQR
        ? Number(scannedData.qty || 0)
        : 1;
  
      const existingIndex = cartons.findIndex(
        (item) =>
          String(item.carton_id) ===
            String(scannedData.carton_id) &&
          String(item.medicine_id) ===
            String(scannedData.medicine_id)
      );
  
      // Carton QR scanned again
      if (isCartonQR && existingIndex !== -1) {
        handleSuccess("Medicine already added");
        setQrCode("");
        return;
      }
  
      // Medicine QR scanned again
      if (!isCartonQR && existingIndex !== -1) {
        const updatedCartons = [...cartons];
  
        updatedCartons[existingIndex] = {
          ...updatedCartons[existingIndex],
          qty:
            Number(
              updatedCartons[existingIndex].qty || 0
            ) + qtyToAdd,
        };
  
        setCartons(updatedCartons);
  
        handleSuccess(
          "Quantity updated successfully"
        );
  
        setQrCode("");
        return;
      }
  
      let mfg_date = scannedData.mfg_date || "";
  
      const newCarton = {
        carton_id:
          scannedData.carton_id ||
          scannedData.carton_code ||
          code,
  
        medicine_id:
          scannedData.medicine_id || "",
  
        medicine_name:
          scannedData.medicine_name || "",
  
        batch_no:
          scannedData.batch_no || "",
  
        qty: qtyToAdd,
  
        manufacturing_date: mfg_date,
      };
  
      setCartons((prev) => [
        ...prev,
        newCarton,
      ]);
  
      handleSuccess(
        "Medicine added successfully"
      );
  
      setQrCode("");
  
    } catch (error) {
  
      console.error(
        "Error processing scan:",
        error
      );
  
      showErrorAlert(
        "Invalid QR code format"
      );
  
      setQrCode("");
    }
  };
  const handleOpenAddModal = () => {
    setEditingIndex(null);
    setFormData({
      medicine_id: "",
      medicine_name: "",
      batch_no: "",
      carton_id: "",
      qty: "",
      manufacturing_date: "",
    });
    setModalErrors({});
    setShowAddModal(true);
  };

  const handleQuantityChange = (index, value) => {
    const updatedCartons = [...cartons];
    updatedCartons[index].qty = value;
    setCartons(updatedCartons);
    if (errors[`qty_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`qty_${index}`];
      setErrors(newErrors);
    }
  };

  const handleFormChange = (field, value) => {
    if (field === "medicine_id") {
      // Handle MedicineDropdown selection
      const medicineId = value?.value || value || "";
      const medicineName = value?.label || "";
      setFormData(prev => ({
        ...prev,
        medicine_id: medicineId,
        medicine_name: medicineName,
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (modalErrors[field]) {
      const newErrors = { ...modalErrors };
      delete newErrors[field];
      setModalErrors(newErrors);
    }
  };

  const [modalErrors, setModalErrors] = useState({});

  const handleSaveCarton = () => {
    const newErrors = {};
    
    if (!formData.medicine_id) newErrors.medicine_id = "Please select medicine";
    if (!formData.batch_no || !formData.batch_no.trim()) newErrors.batch_no = "Please enter batch number";
    if (!formData.qty || parseInt(formData.qty) <= 0) newErrors.qty = "Please enter valid quantity (greater than 0)";
    if (!formData.manufacturing_date) newErrors.manufacturing_date = "Please select manufacturing date";
  
    if (Object.keys(newErrors).length > 0) {
      setModalErrors(newErrors);
      return;
    }
  
    const cartonData = {
      medicine_id: parseInt(formData.medicine_id),
      medicine_name: formData.medicine_name,
      batch_no: formData.batch_no.trim(),
      carton_id: formData.carton_id?.trim() ,
      qty: parseInt(formData.qty),
      manufacturing_date: formData.manufacturing_date,
    };
  
  
    if (editingIndex !== null) {

      const updatedCartons = [...cartons];
      updatedCartons[editingIndex] = cartonData;
    
      setCartons(updatedCartons);
    
      handleSuccess(
        "Medicine updated successfully"
      );
    
    } else {
    
      const existingIndex =
        cartons.findIndex(
          (item) =>
            String(item.medicine_id) ===
              String(
                cartonData.medicine_id
              ) &&
            String(item.batch_no)
              .trim()
              .toLowerCase() ===
              String(
                cartonData.batch_no
              )
                .trim()
                .toLowerCase()
        );
    
      // Same medicine + batch already exists
      if (existingIndex !== -1) {
    
        const updatedCartons = [
          ...cartons,
        ];
    
        updatedCartons[
          existingIndex
        ] = {
          ...updatedCartons[
            existingIndex
          ],
    
          qty:
            Number(
              updatedCartons[
                existingIndex
              ].qty || 0
            ) +
            Number(
              cartonData.qty || 0
            ),
        };
    
        setCartons(updatedCartons);
    
        handleSuccess(
          "Quantity updated successfully"
        );
    
      } else {
    
        // New medicine entry
        setCartons([
          ...cartons,
          cartonData,
        ]);
    
        handleSuccess(
          "Medicine added successfully"
        );
      }
    }
    setShowAddModal(false);
    setEditingIndex(null);
    setFormData({
      medicine_id: "",
      medicine_name: "",
      batch_no: "",
      carton_id: "",
      qty: "",
      manufacturing_date: "",
    });
    setModalErrors({});
  };

  const handleDelete = (index) => {
    if (isViewMode) return;
  
    swal({
      title: "Are you sure?",
      text: "You want to remove this carton?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        const updated = cartons.filter((_, i) => i !== index);
        setCartons(updated);
        swal("Deleted!", "Carton removed successfully.", "success");
      }
    });
  };

  const renderQuantityCell = (item, index) => {
    if (isViewMode) {
      return <span>{item.qty}</span>;
    }
    return (
      <div>
        <input
          type="number"
          className="form-control form-control-sm"
          value={item.qty}
          onChange={(e) => handleQuantityChange(index, e.target.value)}
          min="1"
          style={{ width: "100px" }}
        />
        {errors[`qty_${index}`] && (
          <small className="text-danger">{errors[`qty_${index}`]}</small>
        )}
      </div>
    );
  };

  const calculateTotals = () => {
    const total_medicine = cartons.length;
    const total_qty = cartons.reduce((sum, item) => sum + (parseInt(item.qty) || 0), 0);
    return { total_medicine, total_qty };
  };

  const formatDate = (date) => {
    if (!date) return "";
    
    let d = new Date(date);
    
    if (isNaN(d.getTime())) {
      const parts = String(date).split('-');
      if (parts.length === 3) {
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        
        if (day > 0 && day <= 31 && month > 0 && month <= 12 && year > 1900) {
          return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${year}`;
        }
      }
      return "";
    }
    
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const validateCartons = () => {
    const newErrors = {};
    cartons.forEach((c, index) => {
      if (!c.medicine_id) newErrors[`medicine_${index}`] = "Medicine required";
      if (!c.batch_no) newErrors[`batch_${index}`] = "Batch number required";
      // if (!c.carton_id) newErrors[`carton_${index}`] = "Carton ID required";
      if (!c.qty) newErrors[`qty_${index}`] = "Quantity required";
      if (!c.manufacturing_date) newErrors[`mfg_${index}`] = "Manufacturing date required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedBranch) {
      setErrors({ ...errors, branch: "Please select a branch" });
      return;
    } else {
      const newErrors = { ...errors };
      delete newErrors.branch;
      setErrors(newErrors);
    }

    // if (cartons.length === 0) {
    //   setErrors({ ...errors, cartons: "Please add at least one carton" });
    //   return;
    // }

    if (!validateCartons()) {
      return;
    }

    const { total_medicine, total_qty } = calculateTotals();

    const payload = {
      branch_id: selectedBranch.value,
      scanqrcodedata: JSON.stringify(
        cartons.map((c) => ({
          medicine_id: String(c.medicine_id),
          batch_no: String(c.batch_no),
          carton_id: String(c.carton_id),
          mfg_date: formatDate(c.manufacturing_date),
          qty: String(c.qty),
        }))
      ),
      total_medicine,
      total_qty,
    };

    console.log("========== INWARD STOCK PAYLOAD ==========");
    console.log("Branch ID:", payload.branch_id);
    console.log("Scan QR Code Data:", payload.scanqrcodedata);
    console.log("Total Medicine Count:", payload.total_medicine);
    console.log("Total Quantity:", payload.total_qty);
    console.log("Complete Payload:", JSON.stringify(payload, null, 2));
    console.log("==========================================");

    try {
      setLoading(true);
      const response = await dispatch(addInwardStockAction(payload));
      console.log("API Response:", response);
      
      if (response?.success) {
        setTimeout(() => {
          navigate("/inward-stock");
        }, 1500);
      }
    } catch (err) {
      console.error("Error submitting inward stock:", err);
    } finally {
      setLoading(false);
    }
  };

  const { total_medicine, total_qty } = calculateTotals();

  const isFormValid = () => {
    if (!selectedBranch) return false;
    if (cartons.length === 0) return false;
    return cartons.every(c => 
      c.medicine_id && c.batch_no &&  c.qty && c.manufacturing_date
    );
  };

  return (
    <div className="row">
      <div className="col-xl-12">
      
      <div className="d-flex justify-content-end align-items-center gap-2 mb-3">

{!isViewMode && (
  <button
    type="button"
    className="btn btn-outline-primary"
    onClick={checkCameraPermissions}
  >
    <i className="las la-qrcode me-2"></i>
    Scan  QR
  </button>
)}

{!isViewMode && (
  <Button
    variant="outline-primary"
    onClick={handleOpenAddModal}
  >
    <i className="fas fa-plus me-2"></i>
    Add Manually
  </Button>
)}

<Button
  onClick={() => navigate("/inward-stock")}
  variant="primary"
>
  Back
</Button>

</div>
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-3 mb-3">
                  <label className="form-label">
                    Branch <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={(selected) => setSelectedBranch(selected)}
                    placeholder="Select Branch"
                    isDisabled={isViewMode}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        backgroundColor: isViewMode ? "#f5f5f5" : "white",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                  />
                  {errors.branch && <small className="text-danger">{errors.branch}</small>}
                </div>

               
              </div>

              {showScanner && (
                <div className="modal d-block" style={{ backgroundColor: "rgba(0, 0, 0, 0.7)", zIndex: 1050 }}>
                  <div className="modal-dialog modal-dialog-centered modal-md">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Scan QR Code</h5>
                        <button className="btn-close" onClick={() => setShowScanner(false)}></button>
                      </div>
                      <div className="modal-body text-center">
                        <div id="qr-reader" style={{ width: "100%" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="table-responsive mb-3">
                <table className="table table-bordered table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Medicine</th>
                      <th>Batch Number</th>
                      <th>Carton ID</th>
                      <th>Quantity</th>
                      <th>Manufacturing Date</th>
                      {!isViewMode && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {cartons.length === 0 ? (
                      <tr>
                        <td colSpan={isViewMode ? 6 : 7} className="text-center py-5">
                          <i className="fas fa-box-open fa-3x text-muted mb-3 d-block"></i>
                          <p className="text-muted">No medicine added yet</p>
                          {errors.cartons && <small className="text-danger d-block">{errors.cartons}</small>}
                        </td>
                      </tr>
                    ) : (
                      cartons.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            {item.medicine_name || <span className="text-danger">Not selected</span>}
                            {errors[`medicine_${index}`] && (
                              <div><small className="text-danger">{errors[`medicine_${index}`]}</small></div>
                            )}
                          </td>
                          <td>
                            {item.batch_no || "-"}
                            {errors[`batch_${index}`] && (
                              <div><small className="text-danger">{errors[`batch_${index}`]}</small></div>
                            )}
                          </td>
                          <td>
                            <code>{item.carton_id}</code>
                            {errors[`carton_${index}`] && (
                              <div><small className="text-danger">{errors[`carton_${index}`]}</small></div>
                            )}
                          </td>
                          <td>
                            {renderQuantityCell(item, index)}
                          </td>
                          <td>
                          {formatDate(item.manufacturing_date)}
                            {errors[`mfg_${index}`] && (
                              <div><small className="text-danger">{errors[`mfg_${index}`]}</small></div>
                            )}
                          </td>
                          {!isViewMode && (
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(index)}
                                title="Delete Carton"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {!isViewMode && (
                <div className="d-flex justify-content-end">
                  <button 
                    type="submit"
                    className="btn btn-primary" 
                    disabled={loading || !isFormValid()}
                    title={!isFormValid() ? "Please fill all required fields and add at least one medicine" : ""}
                  >
                    {loading ? (
                      <>
                        <Loader size="sm" /> <span className="ms-2">Saving...</span>
                      </>
                    ) : (
                      "Submit Inward Stock"
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Add/Edit Carton Modal */}
      {!isViewMode && (
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingIndex !== null ? "Edit Carton" : "Add Medicine"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Medicine <span className="text-danger">*</span>
                  </label>
                  <MedicineDropdown
                    isMulti={false}
                    value={formData.medicine_id ? { value: formData.medicine_id, label: formData.medicine_name } : null}
                    onChange={(selected) => handleFormChange("medicine_id", selected)}
                  />
                  {modalErrors.medicine_id && <small className="text-danger">{modalErrors.medicine_id}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Batch Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.batch_no}
                    onChange={(e) => handleFormChange("batch_no", e.target.value)}
                    placeholder="Enter batch number"
                    style={{ height: "45px" }}
                  />
                  {modalErrors.batch_no && <small className="text-danger">{modalErrors.batch_no}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Carton ID 
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.carton_id}
                    onChange={(e) => handleFormChange("carton_id", e.target.value)}
                    placeholder="Enter carton ID (optional)"
                    style={{ height: "45px" }}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Quantity <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.qty}
                    onChange={(e) => handleFormChange("qty", e.target.value)}
                    placeholder="Enter quantity"
                    min="1"
                    style={{ height: "45px" }}
                  />
                  {modalErrors.qty && <small className="text-danger">{modalErrors.qty}</small>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Manufacturing Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.manufacturing_date}
                    onChange={(e) => handleFormChange("manufacturing_date", e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                    style={{ height: "45px" }}
                  />
                  {modalErrors.manufacturing_date && <small className="text-danger">{modalErrors.manufacturing_date}</small>}
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-danger" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveCarton}>
              <i className="fas fa-save me-2"></i>
              {editingIndex !== null ? "Update Medicine" : "Add Medicine"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default AddInwardStock;
